import { motion } from "framer-motion";

const GOLD = "#A9842F";
const GOLD_L = "#C8A24A";
const EMERALD = "#1C3A2B";

const ringPos = (cx, cy, r, deg) => {
  const a = (deg * Math.PI) / 180;
  return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
};

// exact same palette as the floating-leaves canvas
export const LEAF_COLORS = [
  ["#6E9A57", "#46703E"],
  ["#8FAE5D", "#5C8A4E"],
  ["#A9CB7E", "#6E9A57"],
  ["#C8A24A", "#96762C"],
  ["#B5793B", "#85562A"],
];

/* Same leaf silhouette as the canvas leaves drifting across the page */
export const Leaf = ({ ci = 0, transform, opacity = 1 }) => (
  <g transform={transform} opacity={opacity}>
    <path d="M0,-10 C8,-5.5 7,5.5 0,10 C-7,5.5 -8,-5.5 0,-10" fill={`url(#leafG${ci})`} />
    <path d="M0,-8.5 L0,8.5" stroke={LEAF_COLORS[ci][1]} strokeWidth="0.7" opacity="0.6" fill="none" />
  </g>
);

/* Leaves arranged as an Islamic rosette, slowly rotating */
export const LeafRosette = ({ cx, cy, count = 8, dist = 16, scale = 1, dir = "geo-cw", delay = 0, start = 0, palette = [0, 1, 2], center = true }) => (
  <motion.g
    initial={{ scale: 0, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    style={{ transformOrigin: `${cx}px ${cy}px` }}
  >
    <g className={dir} style={{ transformOrigin: `${cx}px ${cy}px` }}>
      {[...Array(count)].map((_, i) => {
        const deg = start + (i * 360) / count;
        const a = (deg * Math.PI) / 180;
        const x = cx + dist * Math.cos(a);
        const y = cy + dist * Math.sin(a);
        return (
          <Leaf
            key={i}
            ci={palette[i % palette.length]}
            transform={`translate(${x},${y}) rotate(${deg + 90}) scale(${scale})`}
          />
        );
      })}
      {center && <circle cx={cx} cy={cy} r={3.2 * scale} fill={GOLD_L} />}
    </g>
  </motion.g>
);

const BRANCHES = [
  ["M260,560 C252,500 258,420 260,330", 11, 8, 0.2],
  ["M260,478 C234,454 204,398 184,298", 8, 5.5, 0.45],
  ["M260,478 C286,454 316,398 336,298", 8, 5.5, 0.45],
];

/* A tree whose canopy is a constellation of Islamic geometric motifs:
   gold-edged branches draw themselves in, then a girih lattice appears
   and eight-pointed stars bloom outward, each slowly rotating. */
/* The swaying leaf-rosette tree as an embeddable SVG group */
export const LeafTreeFigure = () => {
  const CX = 260;
  const CY = 210;
  const ring = [...Array(8)].map((_, i) => ringPos(CX, CY, 112, i * 45));
  const outer = [...Array(8)].map((_, i) => {
    const deg = i * 45 + 22.5;
    return [...ringPos(CX, CY, 165, deg), deg];
  });
  const dots = [...Array(16)].map((_, i) => ringPos(CX, CY, 196, i * 22.5));

  return (
    <g className="geo-sway" style={{ transformOrigin: "260px 560px" }}>
          {/* trunk & branches: gold edge beneath emerald core, drawn in */}
          {BRANCHES.map(([d, wg, we, delay], i) => (
            <g key={i}>
              <motion.path
                d={d}
                stroke={GOLD_L}
                strokeWidth={wg}
                fill="none"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.1, delay, ease: "easeOut" }}
              />
              <motion.path
                d={d}
                stroke={EMERALD}
                strokeWidth={we}
                fill="none"
                strokeLinecap="round"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.1, delay, ease: "easeOut" }}
              />
            </g>
          ))}

          {/* girih lattice connecting the canopy */}
          <motion.g
            stroke={GOLD}
            strokeOpacity="0.3"
            strokeWidth="1"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.15, duration: 0.9 }}
          >
            {ring.map(([x, y], i) => (
              <line key={`c${i}`} x1={CX} y1={CY} x2={x} y2={y} />
            ))}
            {ring.map(([x, y], i) => {
              const [nx, ny] = ring[(i + 1) % 8];
              return <line key={`n${i}`} x1={x} y1={y} x2={nx} y2={ny} />;
            })}
          </motion.g>

          {/* leaf rosette heart — same leaves as the ones floating by */}
          <LeafRosette cx={CX} cy={CY} count={10} dist={36} scale={2.1} dir="geo-ccw" delay={1.0} palette={[0, 1, 2]} center={false} />
          <LeafRosette cx={CX} cy={CY} count={8} dist={16} scale={1.35} dir="geo-cw" delay={1.2} start={18} palette={[2, 3, 1]} />

          {/* ring of blooming leaf clusters */}
          {ring.map(([x, y], i) => (
            <LeafRosette
              key={i}
              cx={x}
              cy={y}
              count={7}
              dist={16}
              scale={1.5}
              dir={i % 2 === 0 ? "geo-cw" : "geo-ccw"}
              delay={1.3 + i * 0.08}
              start={i * 22.5}
              palette={i % 2 === 0 ? [1, 0, 2] : [2, 1, 0]}
            />
          ))}

          {/* outer golden leaves pointing outward */}
          {outer.map(([x, y, deg], i) => (
            <motion.g
              key={i}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, delay: 1.95 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              style={{ transformOrigin: `${x}px ${y}px` }}
            >
              <Leaf ci={i % 2 === 0 ? 3 : 4} transform={`translate(${x},${y}) rotate(${deg + 90}) scale(1.45)`} />
            </motion.g>
          ))}

          {/* halo of dots */}
          <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.3, duration: 1 }}>
            {dots.map(([x, y], i) => (
              <circle key={i} cx={x} cy={y} r="2.2" fill={GOLD} opacity="0.45" />
            ))}
          </motion.g>
    </g>
  );
};

export const GeometricTree = () => {
  return (
    <div
      data-testid="hero-geometric-tree"
      className="relative w-[440px] h-[540px] lg:w-[500px] lg:h-[600px]"
      aria-hidden="true"
    >
      <svg viewBox="0 0 520 600" className="w-full h-full">
        <defs>
          {LEAF_COLORS.map(([c1, c2], i) => (
            <linearGradient key={i} id={`leafG${i}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={c1} />
              <stop offset="100%" stopColor={c2} />
            </linearGradient>
          ))}
        </defs>
        <LeafTreeFigure />

        {/* floating satellite leaves */}
        <g className="animate-float">
          <Leaf ci={3} transform="translate(72,130) rotate(-30) scale(1.1)" />
        </g>
        <g className="animate-float" style={{ animationDelay: "-2.5s" }}>
          <Leaf ci={1} transform="translate(452,150) rotate(40) scale(0.9)" />
        </g>
        <g className="animate-float" style={{ animationDelay: "-4.5s" }}>
          <Leaf ci={4} transform="translate(440,430) rotate(15) scale(1)" />
        </g>
      </svg>
    </div>
  );
};
